import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { NavArrowRight, Plus } from 'iconoir-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { sidebarSectionHeaderLabelClass } from './sidebar-section-label';
import { AgentGroupBy, AgentsGroupMenu } from './agents-group-menu';

interface SidebarSectionProps {
  title: string;
  icon?: IconName;
  children: React.ReactNode;
  onAddClick?: (e: React.MouseEvent) => void;
  addLabel?: string;
  defaultOpen?: boolean;
  variant?: 'default' | 'section';
  groupBy?: AgentGroupBy;
  onGroupByChange?: (value: AgentGroupBy) => void;
  tourTarget?: string;
  fill?: boolean;
}

export function SidebarCollapsibleSection({
  title,
  icon,
  children,
  onAddClick,
  addLabel,
  defaultOpen = false,
  variant = 'default',
  groupBy = 'agents',
  onGroupByChange,
  tourTarget,
  fill = false,
}: SidebarSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isGroupMenuOpen, setIsGroupMenuOpen] = useState(false);
  const { theme } = useTheme();
  const isSection = variant === 'section';
  const showGroupMenu = isSection && Boolean(onGroupByChange);
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-400';
  const actionColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const sectionActionClass = cn(
    'flex cursor-pointer items-center justify-center group-data-[collapsible=icon]:hidden dark:hover:bg-sidebar-pill-dark',
    actionColor,
    theme === 'dark' ? 'hover:text-true-white' : 'hover:text-grey-900'
  );

  const addButtonLabel = addLabel ?? `Add ${title}`;
  const addButton = onAddClick ? (
    <button
      type="button"
      onClick={onAddClick}
      className={cn(sectionActionClass, 'size-5')}
      aria-label={addButtonLabel}
    >
      <Plus width={20} height={20} strokeWidth={1.5} />
    </button>
  ) : null;

  if (isSection) {
    return (
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className={cn('group/section w-full', fill && isOpen && 'flex min-h-0 flex-1 flex-col')}
      >
        <div
          data-buddy-tour-target={tourTarget}
          className="flex w-full shrink-0 items-center justify-between rounded-nav px-2.5 pb-2 pt-3"
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex min-h-3.5 min-w-0 flex-1 cursor-pointer items-center gap-0.5 text-left group-data-[collapsible=icon]:justify-center"
            >
              <span
                className={cn(
                  sidebarSectionHeaderLabelClass,
                  'whitespace-nowrap group-data-[collapsible=icon]:hidden',
                  titleColor
                )}
              >
                {title}
              </span>
              <NavArrowRight
                width={16}
                height={16}
                strokeWidth={1.75}
                className={cn(
                  'shrink-0 transition-transform duration-200 group-data-[collapsible=icon]:hidden',
                  titleColor,
                  isOpen ? '-rotate-90' : 'rotate-90'
                )}
              />
            </button>
          </CollapsibleTrigger>

          {(onAddClick || showGroupMenu) && (
            <div className="flex h-5 shrink-0 items-center gap-2.5 group-data-[collapsible=icon]:hidden">
              {addButton &&
                (addLabel ? (
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>{addButton}</TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={6}
                        className={cn(
                          'rounded-nav border border-grey-100 bg-true-white px-3 py-1.5 text-sm font-normal leading-heading shadow-primary-soft dark:border-sidebar-border dark:bg-sidebar',
                          actionColor
                        )}
                      >
                        {addLabel}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  addButton
                ))}
              {showGroupMenu && onGroupByChange && (
                <AgentsGroupMenu
                  value={groupBy}
                  onChange={onGroupByChange}
                  open={isGroupMenuOpen}
                  onOpenChange={setIsGroupMenuOpen}
                  triggerClassName={cn(sectionActionClass, 'size-4')}
                />
              )}
            </div>
          )}
        </div>

        <CollapsibleContent
          className={cn(
            'flex flex-col gap-1 data-[state=open]:pt-1 group-data-[collapsible=icon]:hidden',
            fill && 'min-h-0 flex-1 overflow-hidden'
          )}
        >
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/section w-full">
      <div className="flex w-full items-center justify-between gap-3 px-4 py-1.5 group-data-[collapsible=icon]:justify-center">
        <CollapsibleTrigger asChild>
          <button className="flex flex-1 cursor-pointer items-center gap-3 text-left text-compact font-medium leading-5.5 text-sidebar-foreground hover:text-foreground group-data-[collapsible=icon]:justify-center">
            {icon && <Icon name={icon} size={16} />}
            <span className="group-data-[collapsible=icon]:hidden">{title}</span>
            <ChevronRight
              className={cn(
                'h-3 w-3 transition-transform duration-200 group-data-[collapsible=icon]:hidden',
                isOpen && 'rotate-90'
              )}
            />
          </button>
        </CollapsibleTrigger>

        {onAddClick && (
          <button
            type="button"
            onClick={onAddClick}
            className="cursor-pointer rounded-md p-1 text-muted-foreground transition-opacity hover:bg-muted-foreground/10 hover:text-foreground group-data-[collapsible=icon]:hidden"
          >
            <Plus width={18} height={18} />
          </button>
        )}
      </div>

      <CollapsibleContent className="space-y-1 group-data-[collapsible=icon]:hidden">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
